// A modal panel (docs/cockpit/spec.md): focus moves into it, Tab and Shift+Tab stay inside it (focus
// guards at either end send it back, which also holds when focus leaves an iframe inside), Escape or
// the close button closes it, and focus returns to whatever opened it.
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useWords } from "./i18n/words.js";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea, iframe, [tabindex="0"]:not(.focus-guard)';

export function Dialog({
  title,
  onClose,
  children,
  small = false,
  describedBy,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** As tall as what it holds and narrower, for a short question rather than a document. */
  small?: boolean;
  /** The id of what a screen reader reads out after the title. */
  describedBy?: string;
}) {
  const w = useWords();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    close.current?.focus();
    return () => opener?.focus();
  }, []);

  const focusables = () => [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onClose();
  };

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className={small ? "panel panel-small" : "panel"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        onKeyDown={onKeyDown}
      >
        <span className="focus-guard" tabIndex={0} onFocus={() => focusables().at(-1)?.focus()} />
        <div className="panel-head">
          <h2 id={titleId}>{title}</h2>
          <button ref={close} type="button" className="btn" onClick={onClose}>
            {w.dialogClose}
          </button>
        </div>
        {children}
        <span className="focus-guard" tabIndex={0} onFocus={() => focusables()[0]?.focus()} />
      </div>
    </>
  );
}
